/*
 * DMG – logo a matrice LED animato (proposta B).
 * I LED si accendono colonna per colonna, sfarfallano un attimo e restano accesi.
 * - data-mode="once": si accende una volta (nell'intestazione solo alla prima pagina della visita)
 * - data-mode="loop": ciclo continuo con alcuni LED "difettosi" (pannello in home),
 *   con sotto la scritta "distribuzione" fatta di LED, che si accende dopo DMG
 * Con "riduci movimento" attivo nel sistema il logo resta semplicemente acceso.
 */

const FLK = 6; // tick di sfarfallio di ogni LED appena acceso
const ST = 10; // durata del "vuoto di corrente" dopo l'accensione
const DIM_TICKS = [1, 2, 7]; // tick in cui il logo si abbassa (mai più di 2 cali al secondo)
const FAULTY_RANKS = [2, 15, 20, 33, 44]; // i LED che ogni tanto si spengono nel ciclo continuo: 2 nella D, 2 nella M, 1 nella G
const HOLD = 60; // tick a logo stabile nel ciclo continuo
const DARK = 12; // tick a logo spento prima di ripartire
const SESSION_KEY = 'dmg-led-played';
const WORD_COLS = 62;
const WORD_TICKS = 21; // tick in cui "distribuzione" si accende, da sinistra a destra
const GLYPHS = {
  d: ['00001', '00001', '01101', '10011', '10001', '10011', '01101'],
  i: ['1', '0', '1', '1', '1', '1', '1'],
  s: ['00000', '00000', '01111', '10000', '01110', '00001', '11110'],
  t: ['010', '010', '111', '010', '010', '010', '001'],
  r: ['0000', '0000', '1011', '1100', '1000', '1000', '1000'],
  b: ['10000', '10000', '10110', '11001', '10001', '11001', '10110'],
  u: ['00000', '00000', '10001', '10001', '10001', '10011', '01101'],
  z: ['00000', '00000', '11111', '00010', '00100', '01000', '11111'],
  o: ['00000', '00000', '01110', '10001', '10001', '10001', '01110'],
  n: ['00000', '00000', '10110', '11001', '10001', '10001', '10001'],
  e: ['00000', '00000', '01110', '10001', '11111', '10000', '01111']
};
const WORD_ROWS = [0, 1, 2, 3, 4, 5, 6].map((y) =>
  'distribuzione'
    .split('')
    .map((c) => GLYPHS[c][y])
    .join('0')
);

// numero pseudo-casuale 0–99, sempre uguale per gli stessi (a, b)
function hash(a, b) {
  let h = (a * 374761393 + b * 668265263 + 1013904223) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) % 100;
}

class DmgLedMatrix extends HTMLElement {
  connectedCallback() {
    this.dots = Array.from(this.querySelectorAll('[data-rank]')).sort(
      (a, b) => Number(a.dataset.rank) - Number(b.dataset.rank)
    );
    this.total = this.dots.length;
    this.dipStart = this.total + FLK;
    this.ready = this.dipStart + ST;
    this.tickMs = Math.max(20, Number(this.dataset.tick) || 70);
    this.mode = this.dataset.mode === 'loop' ? 'loop' : 'once';
    this.t = 0;
    this.wordDots = [];
    this.faultyWord = [];
    if (this.mode === 'loop') this.buildWord();

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || !this.total) {
      this.showStatic();
      return;
    }

    if (this.mode === 'once') {
      let played = false;
      try {
        played = window.sessionStorage.getItem(SESSION_KEY) === '1';
      } catch (e) {}
      const inEditor = window.Shopify && window.Shopify.designMode;
      if (played && !inEditor) {
        this.showStatic();
        return;
      }
      try {
        window.sessionStorage.setItem(SESSION_KEY, '1');
      } catch (e) {}
      this.start();
    } else {
      this.render(0);
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => (entry.isIntersecting ? this.start() : this.stop()));
      });
      this.observer.observe(this);
      this.onVisibility = () => (document.hidden ? this.stop() : this.observer && this.start());
      document.addEventListener('visibilitychange', this.onVisibility);
    }
  }

  disconnectedCallback() {
    this.stop();
    if (this.observer) this.observer.disconnect();
    if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
  }

  buildWord() {
    const host = this.querySelector('[data-dmg-word]');
    if (!host || host.childElementCount) return;
    const frag = document.createDocumentFragment();
    WORD_ROWS.forEach((row) => {
      for (let x = 0; x < WORD_COLS; x += 1) {
        const el = document.createElement('i');
        el.className = 'dmg-led__wdot';
        frag.appendChild(el);
        if (row[x] === '1') this.wordDots.push({ el, x });
      }
    });
    host.appendChild(frag);
    this.faultyWord = [this.wordDots[12], this.wordDots[47], this.wordDots[85]];
    this.dipStart += WORD_TICKS;
    this.ready += WORD_TICKS;
  }

  showStatic() {
    this.dots.forEach((dot) => dot.classList.add('is-on'));
    this.wordDots.forEach((dot) => dot.el.classList.add('is-on'));
    this.removeAttribute('data-dim');
    this.dataset.state = 'ready';
  }

  start() {
    if (this.timer) return;
    this.render(this.t);
    this.timer = window.setInterval(() => {
      this.t += 1;
      this.render(this.t);
    }, this.tickMs);
  }

  stop() {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = null;
  }

  dotOn(rank, lt) {
    const d = lt - rank;
    if (d < 0) return false;
    if (d === 0) return true;
    if (d < FLK) return hash(rank, d) < 30 + d * 12;
    return true;
  }

  dimAt(lt) {
    const s = lt - this.dipStart;
    return s >= 0 && s < ST && DIM_TICKS.indexOf(s) >= 0;
  }

  renderWord(pos, t, inHold, powered) {
    this.wordDots.forEach((dot, i) => {
      const d = pos - (this.total + Math.floor(dot.x / 3));
      let on = powered && d >= 0;
      if (on && d > 0 && d < FLK) on = hash(i + 200, d) < 30 + d * 12;
      const k = this.faultyWord.indexOf(dot);
      if (on && inHold && k >= 0 && hash(t, 90 + k * 13) < 26) on = false;
      dot.el.classList.toggle('is-on', on);
    });
  }

  render(t) {
    if (this.mode === 'once') {
      if (t >= this.ready) {
        this.stop();
        this.showStatic();
        return;
      }
      this.dataset.state = 'animating';
      this.toggleAttribute('data-dim', this.dimAt(t));
      this.dots.forEach((dot, i) => dot.classList.toggle('is-on', this.dotOn(i, t)));
      return;
    }

    const period = this.ready + HOLD + DARK;
    const pos = t % period;
    const inHold = pos >= this.ready && pos < this.ready + HOLD;
    const secondDip = inHold && pos - this.ready === 35;
    const powered = pos < this.ready + HOLD;

    this.dataset.state = inHold && !secondDip ? 'hold' : 'animating';
    this.toggleAttribute('data-dim', this.dimAt(pos) || secondDip);
    this.dots.forEach((dot, i) => {
      let on = powered && this.dotOn(i, pos);
      const faulty = FAULTY_RANKS.indexOf(i);
      if (on && inHold && faulty >= 0 && hash(t, 7 + faulty * 11) < 22) on = false;
      dot.classList.toggle('is-on', on);
    });
    this.renderWord(pos, t, inHold, powered);
  }
}

if (!customElements.get('dmg-led-matrix')) {
  customElements.define('dmg-led-matrix', DmgLedMatrix);
}
